import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AttachmentInputComponent } from 'app/shared/components/dynamic-forms/inputs/attachment-input/attachment-input.component';
import { FieldConfig } from 'app/shared/components/dynamic-forms/interfaces/field-config.interface';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { DgNumberPipe } from '../../dg-number.pipe';
import { DocumentGenerateService } from '../../document-generate.service';
import { ItemDetailDialogData, ItemDetailResult } from '../../interfaces/dialog-data.interface';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';

interface StockRow {
  warehouseCode: string;
  warehouseName: string;
  availableStock: number;
  unitLabel: string;
}

// Detalle de artículo por línea: notas, adjuntos y stock por almacén
@Component({
  selector: 'app-item-detail-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AttachmentInputComponent,
    DgNumberPipe,
  ],
  templateUrl: './item-detail-modal.component.html',
  styleUrl: './item-detail-modal.component.scss',
})
export class ItemDetailModalComponent implements OnInit {
  readonly MAX_ATTACHMENTS = 3;
  noteControl = new FormControl('', Validators.maxLength(500));
  attachmentsControl = new FormControl<File[]>([]);
  evidences: any[] = [];
  private objectUrls: string[] = [];

  stockRows: StockRow[] = [];
  stockLoading = false;

  attachmentConfig: FieldConfig = {
    _id: 'line-attachments',
    name: 'lineAttachments',
    label: '',
    type: 'attachment',
    visible: true,
    required: false,
    isTypeMandatory: false,
    position: 0,
    group: '',
    groupPosition: 0,
    multiple: true,
    allowedExtensions: 'pdf,jpg,jpeg,png,webp',
    maxFileSize: 10,
  };

  constructor(
    private dialogRef: MatDialogRef<ItemDetailModalComponent>,
    private service: DocumentGenerateService,
    private handlerMessage: HandlerMessageService,
    @Inject(MAT_DIALOG_DATA) public data: ItemDetailDialogData,
    private readonly i18n: LanguageService
  ) {}

  get item() {
    return this.data.item;
  }

  get readonly(): boolean {
    return this.data.readonly === true;
  }

  ngOnInit(): void {
    this.noteControl.setValue(this.item.note ?? '');
    this.evidences = [...(this.item.evidences ?? [])];
    this.attachmentsControl.setValue([...(this.item.newAttachments ?? [])]);
    if (this.readonly) this.noteControl.disable();
    // descarta el excedente sobre el máximo total (evidencias + nuevos)
    this.attachmentsControl.valueChanges.subscribe((files) => {
      const allowed = Math.max(0, this.MAX_ATTACHMENTS - this.evidences.length);
      if ((files?.length ?? 0) > allowed) {
        this.attachmentsControl.setValue((files as File[]).slice(0, allowed), { emitEvent: false });
        this.handlerMessage.warning({
          message: this.i18n.t('DOCUMENT_GENERATE.ITEM_DETAIL_MODAL.MAX_ATTACHMENTS_MESSAGE', {
            max: this.MAX_ATTACHMENTS,
          }),
        });
      }
    });
    if (this.data.showStock) this.loadStock();
  }

  get newFiles(): File[] {
    return this.attachmentsControl.value ?? [];
  }

  removeEvidence(index: number): void {
    this.evidences.splice(index, 1);
  }

  removeNewFile(index: number): void {
    const files = [...this.newFiles];
    files.splice(index, 1);
    this.attachmentsControl.setValue(files);
  }

  openEvidence(file: any): void {
    if (file?.link) window.open(file.link, '_blank');
  }

  // abre un archivo nuevo (aún no subido) generando una URL temporal
  openNewFile(file: File): void {
    const url = URL.createObjectURL(file);
    this.objectUrls.push(url);
    window.open(url, '_blank');
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  private loadStock(): void {
    const materialId = this.item.material?._id?.toString() ?? this.item._id;
    if (!this.data.salesOfficeId || !materialId) return;
    this.stockLoading = true;
    this.service.fetchStockBySalesOffice(this.data.salesOfficeId, materialId).subscribe({
      next: (res) => {
        const rows = res?.data ?? [];
        this.stockRows = rows.map((r: any) => this.toStockRow(r));
        this.stockLoading = false;
      },
      error: () => {
        this.stockRows = [];
        this.stockLoading = false;
      },
    });
  }

  // resuelve el código y nombre de la unidad del stock desde el material
  private toStockRow(raw: any): StockRow {
    const aums: any[] = this.item.material?.alternalUnitOfMeasures ?? [];
    const unit = aums.find(
      (a) => a?.unitOfMeasure?._id?.toString() === raw.unitOfMeasureId?.toString(),
    )?.unitOfMeasure;
    const code = unit?.code ?? '';
    const name = unit?.name ?? '';
    return {
      warehouseCode: raw.warehouseCode ?? '-',
      warehouseName: raw.warehouseName ?? '-',
      availableStock: Number(raw.availableStock ?? 0),
      unitLabel: code && name ? `${code} - ${name}` : code || name || '-',
    };
  }

  save(): void {
    const result: ItemDetailResult = {
      note: (this.noteControl.value ?? '').trim() || undefined,
      evidences: this.evidences,
      newAttachments: this.newFiles,
    };
    this.dialogRef.close(result);
  }

  close(): void {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls = [];
    this.dialogRef.close();
  }
}
